import PropTypes from 'prop-types';
import Dynamic from 'next/dynamic';
import Image from 'next/image';
import { useSelector, useDispatch } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import { useUser } from '@auth0/nextjs-auth0/client';
import { Grid, Typography, Box, ListItem } from '@mui/material';
import MuiAlert from '@mui/material/Alert';

import { UpdateAdditionalService } from 'store/actions/schoolDetailActions';
import { sortBySelected } from 'utils/arrayUtils';
import { shouldUnlockPrice } from 'utils/lead';

export const PriceComponent = Dynamic(
  () => import('components/PriceComponent'),
  { ssr: false }
);

export const AdditionalItemButtonComponent = Dynamic(
  () =>
    import(
      'components/SchoolsComponents/BuildPackageComponent/AdditionalItemButtonComponent'
    ),
  { ssr: false }
);

export const UnlockEstimateComponent = Dynamic(
  () => import('components/UnlockEstimateComponent'),
  { ssr: false }
);

export const InsuranceComponent = Dynamic(
  () => import('components/InsuranceComponent'),
  { ssr: false }
);

function AdditionalListItemComponent({ dateParams }) {
  const dispatch = useDispatch();
  const { additionalServices, location, accommodationGroups } = useSelector(
    (store) => store.schoolDetailReducer
  );
  const { insurances } = location.city.country;
  const hasSelectedAccommodation = accommodationGroups.some(
    (ag) => ag.selected
  );
  const { user } = useUser();
  const { lead } = useSelector((store) => store.studentReducer);
  const filterFromWeeks = (as) =>
    as.fromWeeks === null || as.fromWeeks <= dateParams.courseWeeks;
  const toggleAdditional = (additionalSelected) => {
    if (additionalSelected?.hasAccommodation) {
      if (hasSelectedAccommodation === false) return;
    }

    if (additionalSelected?.title.includes('OSHC')) {
      const additionalIsOSHC = additionalServices?.filter((a) =>
        a.title.includes('OSHC')
      );
      additionalIsOSHC?.forEach((additionalOSHC) => {
        if (additionalOSHC?.id === additionalSelected?.id) {
          additionalOSHC.selected = !additionalOSHC?.selected;
        } else {
          additionalOSHC.selected = false;
        }
      });
    } else {
      const additionalIsInsurance = additionalServices?.filter(
        (a) => !a.title.includes('OSHC')
      );
      additionalIsInsurance?.forEach((additionalInsurance) => {
        if (additionalInsurance?.id === additionalSelected?.id)
          additionalInsurance.selected = !additionalInsurance?.selected;
      });
    }

    UpdateAdditionalService(dispatch, additionalServices);
  };

  const isSelected = (additional) => {
    if (additional?.hasAccommodation && !hasSelectedAccommodation) return false;

    if (additional?.required) return true;

    return false;
  };

  const showPriceComponent = (additional) => {
    if (
      additional?.description.toUpperCase().includes('OSHC') &&
      !additional?.priceExchanged
    )
      return null;

    return (
      <PriceComponent
        value={additional?.priceExchanged}
        fallBackText="Grátis"
      />
    );
  };

  const isCampaign = (id) =>
    [
      1224, 1225, 1226, 1227, 1228, 1229, 1230, 1231, 1232, 1233, 1234, 1235,
      1236, 1237, 1238, 1239,
    ].includes(id);

  return (
    <Box>
      {additionalServices.length + insurances.length === 0 ? (
        <Typography
          sx={{ textTransform: 'uppercase', textAlign: 'center' }}
          variant="h6"
        >
          Não encontramos nenhum adicional no período selecionado
        </Typography>
      ) : (
        <>
          {additionalServices
            .sort(sortBySelected)
            .filter(filterFromWeeks)
            .map((additional) => {
              const isToChangeStyle = isCampaign(additional?.id);

              return (
                <ListItem disableGutters key={additional?.id}>
                  <Box
                    textAlign="left"
                    flexGrow={1}
                    border={1}
                    borderRadius="4px"
                    borderColor={
                      isSelected(additional) || additional?.selected
                        ? 'text.primary'
                        : '#C3C2C2E8'
                    }
                    boxShadow={
                      isSelected(additional) || additional?.selected ? 5 : 0
                    }
                    p={2}
                    sx={{
                      background: isToChangeStyle
                        ? 'linear-gradient(270deg, #258DBF 20%, #054F77 100%)'
                        : '#ffffff',
                    }}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Box
                          display="flex"
                          alignItems={{ xs: 'flex-start', md: 'center' }}
                          flexDirection={{ xs: 'column-reverse', md: 'row' }}
                          width="100%"
                          gap={{ xs: 0, md: 2 }}
                        >
                          <Typography
                            variant="h6"
                            component="h4"
                            sx={{ letterSpacing: 0, marginBottom: '8px' }}
                            fontWeight={800}
                            color={isToChangeStyle ? '#FFFFFF' : 'text.primary'}
                          >
                            {additional?.title}
                          </Typography>

                          {isToChangeStyle && (
                            <Image
                              alt="Mês do consumidor"
                              height={50}
                              loader={({ src, width }) => `${src}?w=${width}`}
                              priority
                              src="https://res.cloudinary.com/intercambiodireto-com/image/upload/q_auto:good/v1744403956/Others/Campaigns/business-tag_ojkzdk"
                              width={104}
                            />
                          )}
                        </Box>

                        {additional?.hasAccommodation ? (
                          <MuiAlert
                            sx={{
                              color: isToChangeStyle ? '#FFFFFF' : '',
                              mt: isToChangeStyle ? 1 : 0,
                            }}
                            variant="outlined"
                            severity="warning"
                          >
                            Obrigatório reservar acomodação da escola
                          </MuiAlert>
                        ) : (
                          <Box
                            color={isToChangeStyle ? '#FFFFFF' : 'text.primary'}
                          >
                            {ReactHtmlParser(additional?.description)}
                          </Box>
                        )}
                      </Grid>

                      <Grid container alignItems="center" item xs={12} sm={6}>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            filter: shouldUnlockPrice(lead, user)
                              ? 'none'
                              : 'blur(6px)',
                            userSelect: shouldUnlockPrice(lead, user)
                              ? 'initial'
                              : 'none',
                          }}
                        >
                          <Typography
                            variant="h6"
                            color={isToChangeStyle ? '#FFFFFF' : 'text.primary'}
                            sx={{
                              fontWeight: 'bold',
                              margin: '8px auto',
                              textAlign: 'center',
                            }}
                          >
                            {showPriceComponent(additional)}
                          </Typography>
                        </Grid>
                        {shouldUnlockPrice(lead, user) ? (
                          <Grid item xs={12} sm={6}>
                            <AdditionalItemButtonComponent
                              additional={additional}
                              isToChangeStyle={isToChangeStyle}
                              hasSelectedAccommodation={
                                hasSelectedAccommodation
                              }
                              toggleAdditional={toggleAdditional}
                            />
                          </Grid>
                        ) : (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ '& > button': { minHeight: '42px' } }}>
                              <UnlockEstimateComponent />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </ListItem>
              );
            })}

          {insurances && (
            <InsuranceComponent
              insurances={insurances}
              dateParams={dateParams}
            />
          )}
        </>
      )}
    </Box>
  );
}

AdditionalListItemComponent.propTypes = {
  dateParams: PropTypes.instanceOf(Object).isRequired,
};

export default AdditionalListItemComponent;
